import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import communicationApi from '../../../../services/communicationApi';
import { MessageComposition, Template } from '../../../../types/communication';
import { TemplateKind, templateKind } from './templateKinds';

export interface TemplateListFilters {
    search: string;
    kind: TemplateKind | '';
    link: 'FREE' | 'LINKED' | '';
    status: 'ACTIVE' | 'INACTIVE' | '';
}

const EMPTY_FILTERS: TemplateListFilters = { search: '', kind: '', link: '', status: '' };

interface Paging {
    page: number;
    pageSize: number;
    field: string;
    order: 'ASC' | 'DESC';
    initialSearch?: string;
}

export const useTemplateList = ({ page, pageSize, field, order, initialSearch = '' }: Paging) => {
    const [filters, setFilters] = useState<TemplateListFilters>({ ...EMPTY_FILTERS, search: initialSearch });
    const [templates, setTemplates] = useState<Template[]>([]);
    const [compositions, setCompositions] = useState<MessageComposition[]>([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [templatePage, compositionPage] = await Promise.all([
                communicationApi.listTemplates({ pageSize: 9999 }),
                communicationApi.listMessageCompositions({ pageSize: 9999 }),
            ]);
            setTemplates(templatePage.list);
            setCompositions(compositionPage.list);
        } catch {
            toast.error('Falha ao carregar modelos.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const compositionOf = useCallback(
        (template: Template) => compositions.find((c) => c.id === template.messageCompositionId),
        [compositions],
    );

    const filtered = useMemo(() => {
        const term = filters.search.trim().toLocaleLowerCase('pt-BR');
        const list = templates.filter(
            (t) =>
                (!term || `${t.name} ${t.description}`.toLocaleLowerCase('pt-BR').includes(term)) &&
                (!filters.kind || templateKind(t) === filters.kind) &&
                (!filters.link || (filters.link === 'LINKED') === Boolean(t.messageCompositionId)) &&
                (!filters.status || t.status === filters.status),
        );
        const key = field === 'updatedAt' ? 'updatedAt' : 'name';
        return [...list].sort((a, b) => a[key].localeCompare(b[key], 'pt-BR') * (order === 'DESC' ? -1 : 1));
    }, [templates, filters, field, order]);

    const kindCounts = useMemo(
        () =>
            templates.reduce<Record<string, number>>(
                (acc, t) => ({ ...acc, [templateKind(t)]: (acc[templateKind(t)] ?? 0) + 1 }),
                { ALL: templates.length },
            ),
        [templates],
    );

    return {
        filters,
        updateFilters: (patch: Partial<TemplateListFilters>) => setFilters((current) => ({ ...current, ...patch })),
        clearFilters: () => setFilters(EMPTY_FILTERS),
        rows: filtered.slice(page * pageSize, page * pageSize + pageSize),
        total: filtered.length,
        totalPages: Math.max(1, Math.ceil(filtered.length / pageSize)),
        kindCounts,
        compositionOf,
        loading,
        reload: load,
    };
};
